import { TFunction } from 'i18next';
import * as Yup from 'yup';
import {
  FloodingCreateUpdateData,
  Flooding,
  RelatedFlooding,
} from 'features/flooding/flooding.types';
import { NotificationType } from 'features/notifications/notifications.types';
import { getLinksValidationSchema } from 'components/FormLinks';
import {
  getInitialEvacZonesVisibleState,
  normalizeLinkValues,
  parseFormLinks,
  validateChildGeoEventsForEvacZones,
} from 'features/geoEvents/geoEvent.utils';
import { GeoEventUrgencyLevel } from 'features/geoEvents/geoEvent.types';
import {
  looksLikeGeneralArea,
  maybeTransformEmptyRichTextContentToNull,
} from 'shared/utils';
import { hasEvacContent } from 'features/evacZones/utils/hasEvacContent';
import { EvacZone, FormEvacZoneStatus } from 'shared/types';
import { getEvacuationFieldsValidationSchema } from 'features/evacZones/utils/getEvacuationFieldsValidationSchema';
import { getReportMedia } from 'components/MediaForm';
import {
  buildReporterNotesPayload,
  reporterNotesDefaultFormValues,
  reporterNotesValidationSchema,
} from 'features/reporterNotes/reporterNotes.utils';
import { getReportMessageValidationSchema } from 'features/wildfires/utils/getReportMessageValidationSchema';
import { validateDeactivationForEvacZones } from 'features/geoEvents/validateDeactivationForEvacZones';
import { getEvacZoneStatusesValidationSchema } from 'features/geoEvents/utils/getEvacZoneStatusesValidationSchema';
import {
  HazardListItem,
  RelatedHazardGeoEvent,
} from 'features/hazards/hazard.types';
import { FloodingFormValues } from './FloodingForm.types';

export const getValidationSchema = (
  t: TFunction,
  regionEvacZones: EvacZone[],
  flooding?: Flooding,
): Yup.SchemaOf<object> => {
  const regionHasEvacZones = regionEvacZones.length > 0;

  return Yup.object().shape(
    {
      name: Yup.string().required(t('createEditFlooding.inputs.name.required')),
      areaImpacted: Yup.string(),
      parentFlooding: Yup.object().nullable(),
      ...getEvacZoneStatusesValidationSchema(
        t,
        regionEvacZones,
        flooding?.evacZoneStatuses || [],
      ),
      childFloodings: Yup.array().test(
        'is-update-valid',
        '',
        function (childFloodings: RelatedFlooding[] = []) {
          const result = validateChildGeoEventsForEvacZones({
            childGeoEvents: childFloodings,
            evacZoneStatuses:
              (this.parent.evacZoneStatuses as FormEvacZoneStatus[]) ?? [],
            originalChildGeoEvents: flooding?.childGeoEvents?.filter(
              (child): child is RelatedFlooding =>
                child.geoEventType === 'flooding',
            ),
          });

          if (result.valid) return true;

          return this.createError({
            message: t(
              'createEditFlooding.inputs.childFloodings.invalidError',
              {
                counties: result.missingRegionNames,
                incidents: result.missingChildIncidents,
              },
            ),
          });
        },
      ),
      regions: Yup.array().when('childFloodings', {
        is: (childFloodings: RelatedFlooding[]) =>
          childFloodings && childFloodings.length > 0,
        then: Yup.array().min(0),
        otherwise: Yup.array().min(
          1,
          t('editGeoEvent.inputs.counties.required'),
        ),
      }),
      status: Yup.string()
        .required(t('createEditFlooding.inputs.status.required'))
        .test(
          'no-deactivation-with-evac-zones',
          t('createEditFlooding.inputs.status.deactivationBlockedByEvacZones'),
          function (value) {
            return validateDeactivationForEvacZones({
              isDeactivating: value === 'inactive',
              evacZoneStatuses: this.parent.evacZoneStatuses ?? [],
            });
          },
        ),
      notificationType: Yup.string().required(
        t('createEditFlooding.inputs.notificationType.required'),
      ),
      reportMessage: Yup.string().when([], {
        is: () => !flooding?.id,
        then: getReportMessageValidationSchema(t),
        otherwise: Yup.string(),
      }),
      lat: Yup.number(),
      lng: Yup.number(),
      expandLinks: Yup.boolean(),
      links: getLinksValidationSchema(t),
      expandInternalLinks: Yup.boolean(),
      internalLinks: getLinksValidationSchema(t),
      reporterOnlyNotes: Yup.string().nullable(),
      ...reporterNotesValidationSchema,
      activeEvacuations: Yup.boolean(),
      ...getEvacuationFieldsValidationSchema(t, regionHasEvacZones),
      confirmSelectedZones: Yup.boolean().when('confirmSelectedZones', {
        is: (exist: boolean) => typeof exist === 'boolean',
        then: Yup.boolean().oneOf([true]),
        otherwise: Yup.boolean(),
      }),
      confirmAdditionalRegions: Yup.boolean().when('confirmAdditionalRegions', {
        is: (exist: boolean) => typeof exist === 'boolean',
        then: Yup.boolean().oneOf([true]),
        otherwise: Yup.boolean(),
      }),
      confirmRemovedRegions: Yup.boolean().when('confirmRemovedRegions', {
        is: (exist: boolean) => typeof exist === 'boolean',
        then: Yup.boolean().oneOf([true]),
        otherwise: Yup.boolean(),
      }),
      urgency: Yup.string().oneOf(Object.values(GeoEventUrgencyLevel)),
      manualDeactivationStarted: Yup.boolean()
        .nullable()
        .test(
          'no-deactivation-with-evac-zones',
          t('createEditFlooding.inputs.status.deactivationBlockedByEvacZones'),
          function (value) {
            return validateDeactivationForEvacZones({
              isDeactivating: value === true,
              evacZoneStatuses: this.parent.evacZoneStatuses ?? [],
            });
          },
        ),
    },
    [
      ['confirmSelectedZones', 'confirmSelectedZones'],
      ['confirmAdditionalRegions', 'confirmAdditionalRegions'],
      ['confirmRemovedRegions', 'confirmRemovedRegions'],
    ],
  );
};

export const getDefaultValues = (input: {
  flooding?: Flooding;
  latitude?: number;
  longitude?: number;
  initialAlertCameraId?: string;
}): FloodingFormValues => {
  const { flooding, latitude, longitude, initialAlertCameraId } = input;
  const initialStatus = flooding?.isActive ?? true;

  return {
    name: flooding?.name || '',
    areaImpacted: flooding?.data.areaImpacted || '',
    regions: flooding?.regions || [],
    status: initialStatus ? 'active' : 'inactive',
    notificationType: flooding?.notificationType || NotificationType.Silent,
    lat: flooding?.lat || latitude || 0,
    lng: flooding?.lng || longitude || 0,
    expandLinks: Boolean(flooding?.data.links?.length),
    links: parseFormLinks(flooding?.data.links || []),
    expandInternalLinks: Boolean(flooding?.data.internalLinks?.length),
    internalLinks: parseFormLinks(flooding?.data.internalLinks || []),
    reporterOnlyNotes: flooding?.data.reporterOnlyNotes || '',
    ...reporterNotesDefaultFormValues,
    activeEvacuations: getInitialEvacZonesVisibleState(flooding),
    evacZoneStatuses: flooding?.evacZoneStatuses || [],
    unstructuredEvacuationRegionIds:
      flooding?.data.unstructuredEvacuationRegionIds ?? [],
    temporarilyDisplayEvacZones:
      flooding?.data.temporarilyDisplayEvacZones ?? false,
    customOrders: Boolean(flooding?.data.hasCustomEvacuationOrders),
    customWarnings: Boolean(flooding?.data.hasCustomEvacuationWarnings),
    customAdvisories: Boolean(flooding?.data.hasCustomEvacuationAdvisories),
    customShelterInPlace: Boolean(
      flooding?.data.hasCustomEvacuationShelterInPlace,
    ),
    evacuationOrders: flooding?.data.evacuationOrders || '',
    evacuationWarnings: flooding?.data.evacuationWarnings || '',
    evacuationNotes: flooding?.data.evacuationNotes || '',
    evacuationAdvisories: flooding?.data.evacuationAdvisories || '',
    evacuationShelterInPlace: flooding?.data.evacuationShelterInPlace || '',
    reportNotificationType: NotificationType.Normal,
    reportMessage: '',
    asset: initialAlertCameraId ? 'alertCamera' : null,
    media: null,
    fileType: '',
    embedUrl: '',
    mediaLat: '',
    mediaLng: '',
    mediaAz: 0,
    urgency: flooding?.data.urgency || GeoEventUrgencyLevel.Standard,
    childFloodings: (flooding?.childGeoEvents?.filter(
      (ge) => ge.geoEventType === 'flooding',
    ) || []) as RelatedFlooding[],
    parentFlooding: (flooding?.parentGeoEvents?.[0] as RelatedFlooding) || null,
    manualDeactivationStarted:
      typeof flooding?.data.manualDeactivationStarted === 'boolean'
        ? flooding?.data.manualDeactivationStarted
        : null,
  };
};

export const floodingFormValuesToServerValues = (
  values: FloodingFormValues,
  mediaUrl?: string,
  regionHasEvacZones = true,
  isCriticalInitialReport = false,
): FloodingCreateUpdateData => {
  const releasePendingZoneUids = values.releasePendingZoneUids?.length
    ? values.releasePendingZoneUids
    : undefined;

  const floodingData: FloodingCreateUpdateData = {
    name: values.name,
    regions: values.regions,
    isActive: values.status === 'active',
    notificationType: isCriticalInitialReport
      ? NotificationType.Critical
      : values.notificationType,
    geoEventType: 'flooding',
    lat: values.lat,
    lng: values.lng,
    reporterManaged: true,
    temporarilyDisplayEvacZones: values.temporarilyDisplayEvacZones,
    data: {
      geoEventType: 'flooding',
      isParent: values.childFloodings.length > 0,
      areaImpacted: values.areaImpacted || null,
      links: normalizeLinkValues(values.links),
      internalLinks: normalizeLinkValues(values.internalLinks),
      reporterOnlyNotes: maybeTransformEmptyRichTextContentToNull(
        values.reporterOnlyNotes,
      ),
      reporterOnlyNotesDateModified: null,
      temporarilyDisplayEvacZones: values.temporarilyDisplayEvacZones,
      hasCustomEvacuationOrders: hasEvacContent({
        evacContent: values.evacuationOrders,
        customEvac: values.customOrders,
        hasEvacZones: regionHasEvacZones,
      }),
      hasCustomEvacuationWarnings: hasEvacContent({
        evacContent: values.evacuationWarnings,
        customEvac: values.customWarnings,
        hasEvacZones: regionHasEvacZones,
      }),
      hasCustomEvacuationAdvisories: hasEvacContent({
        evacContent: values.evacuationAdvisories,
        customEvac: values.customAdvisories,
        hasEvacZones: regionHasEvacZones,
      }),
      hasCustomEvacuationShelterInPlace: hasEvacContent({
        evacContent: values.evacuationShelterInPlace,
        customEvac: values.customShelterInPlace,
        hasEvacZones: regionHasEvacZones,
      }),
      evacuationOrders: maybeTransformEmptyRichTextContentToNull(
        values.evacuationOrders,
      ),
      evacuationWarnings: maybeTransformEmptyRichTextContentToNull(
        values.evacuationWarnings,
      ),
      evacuationNotes: maybeTransformEmptyRichTextContentToNull(
        values.evacuationNotes,
      ),
      evacuationAdvisories: maybeTransformEmptyRichTextContentToNull(
        values.evacuationAdvisories,
      ),
      evacuationShelterInPlace: maybeTransformEmptyRichTextContentToNull(
        values.evacuationShelterInPlace,
      ),
      urgency: values.urgency,
      manualDeactivationStarted: values.manualDeactivationStarted,
      unstructuredEvacuationRegionIds: values.unstructuredEvacuationRegionIds,
    },
    evacZoneStatuses: values.evacZoneStatuses.map((eZS) => ({
      status: eZS.status,
      evacZone: { id: eZS.evacZone.id },
    })),
    childGeoEvents: values.childFloodings.map((ge) => ({
      id: ge.id,
      geoEventType: ge.geoEventType,
    })),
    parentGeoEvents: values.parentFlooding
      ? [
          {
            id: values.parentFlooding.id,
            geoEventType: values.parentFlooding.geoEventType,
          },
        ]
      : [],
    releasePendingZoneUids,
    reporterNotes: buildReporterNotesPayload(values),
  };

  if (values.reportMessage || values.embedUrl || values.media) {
    floodingData.initialReport = {};
  }
  if (values.reportMessage) {
    floodingData.initialReport!.message = values.reportMessage;
  }
  if (values.embedUrl) {
    floodingData.initialReport!.embedUrl = values.embedUrl;
  }
  if (values.media) {
    floodingData.initialReport!.media = getReportMedia({
      media: values.media,
      fileType: values.fileType,
      lat: values.mediaLat,
      lng: values.mediaLng,
      az: values.mediaAz,
      mediaUrl,
    });
  }

  return floodingData;
};

export const getSubmitButtonKey = ({
  isEditing,
  isCritical,
}: {
  isEditing: boolean;
  isCritical: boolean;
}): string => {
  if (isEditing) return 'createEditFlooding.buttons.update';
  if (isCritical) return 'createEditFlooding.buttons.createCritical';
  return 'createEditFlooding.buttons.create';
};

export const validateAreaImpacted = (
  areaImpacted: string,
  t: TFunction,
): string | null => {
  if (!looksLikeGeneralArea(areaImpacted)) {
    return t('createEditFlooding.inputs.areaImpacted.warning');
  }

  return null;
};

export const deactivationWouldOrphanActiveHazards = ({
  floodingId,
  isDeactivating,
  allHazards,
}: {
  floodingId: number;
  isDeactivating: boolean;
  allHazards: HazardListItem[];
}): boolean => {
  if (!isDeactivating) return false;

  const isParentGeoEvent = (parent: RelatedHazardGeoEvent): boolean =>
    parent.id === floodingId && parent.geoEventType === 'flooding';

  const activeChildHazards = allHazards.filter(
    (hazard) =>
      hazard.parentGeoEvents.some(isParentGeoEvent) && hazard.isActive,
  );

  const hasOrphanedHazards = activeChildHazards.some((hazard) => {
    const activeParents = hazard.parentGeoEvents.filter(
      (parent) => parent.isActive,
    );
    const remainingParents = activeParents.filter(
      (parent) => !isParentGeoEvent(parent),
    );
    return remainingParents.length === 0;
  });

  return hasOrphanedHazards;
};
